import type { JSX } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCandidateEducations } from '../hooks/useCandidateEducations';
import { useEducationDialog } from '../hooks/useEducationDialog';
import { EDUCATION_LEVEL_LABELS } from '@/features/educations/types/education.types';
import type { Education } from '@/features/educations/types/education.types';
import { CandidateEducationFormDialog } from './CandidateEducationFormDialog';

function EducationItem({
  education,
  onEdit,
  onDelete,
}: {
  education: Education;
  onEdit: (edu: Education) => void;
  onDelete: (id: string) => void;
}): JSX.Element {
  return (
    <div className="border rounded p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="font-medium">{EDUCATION_LEVEL_LABELS[education.educationLevel]}</p>
          <p>{education.institution}</p>
          <p className="text-sm text-muted-foreground">
            {education.educationField} • {education.city}, {education.country}
          </p>
          <p className="text-sm text-muted-foreground">
            {education.yearBegin} - {education.yearEnd || 'Present'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={(): void => onEdit(education)}>
            Edit
          </Button>
          <Button variant="destructive" size="sm" onClick={(): void => onDelete(education.id)}>
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

export function CandidateEducationSection(): JSX.Element {
  const { educations, isLoading, deleteEducation } = useCandidateEducations();
  const dialog = useEducationDialog();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">Loading...</CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          Education
          <Button variant="outline" size="sm" onClick={dialog.openCreate}>
            Add Education
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!educations || educations.length === 0 ? (
          <p className="text-muted-foreground">No education records yet.</p>
        ) : (
          <div className="space-y-4">
            {educations.map((edu) => (
              <EducationItem
                key={edu.id}
                education={edu}
                onEdit={dialog.openEdit}
                onDelete={deleteEducation}
              />
            ))}
          </div>
        )}
      </CardContent>
      <CandidateEducationFormDialog
        open={dialog.open}
        onOpenChange={dialog.setOpen}
        mode={dialog.mode}
        education={dialog.education}
      />
    </Card>
  );
}
